import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { changePassword } from 'src/services/Login/ForgotPassword';
import Tostify, { showToast } from 'src/utils/Tostify';
import LogoHeader from 'src/components/header/LogoHeader';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
const generateES6Hash = (data) => {
  const encryptedText = CryptoJS.AES.encrypt(data, process.env.REACT_APP_KEY).toString()
  return encryptedText
}

function ConfirmNewPassword() {
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const location = useLocation();
  const userId = location.state?.userId[0];
  const navigate = useNavigate();
  const roleId = location.state?.userId
  const toggleShowPassword = (field) =>
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  const validateForm = (updatedFormData) => {
    const newErrors = {
      newPassword: !updatedFormData.newPassword
        ? 'New password is required'
        : !PASSWORD_PATTERN.test(updatedFormData.newPassword)
          ? 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character'
          : '',
      confirmPassword: !updatedFormData.confirmPassword
        ? 'Confirm password is required'
        : updatedFormData.newPassword !== updatedFormData.confirmPassword
          ? 'Passwords do not match'
          : '',
    };
    if (userId && updatedFormData.newPassword.includes(userId)) {
      newErrors.newPassword = 'Password should not contain your user ID';
    }
    return newErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setErrors(validateForm(updatedFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault()

    const newErrors = validateForm(formData)
    setErrors(newErrors)

    if (!newErrors.newPassword && !newErrors.confirmPassword) {
      setLoading(true)

      const payload = {
        newPassword: generateES6Hash(formData.newPassword),
        confirmPassword: generateES6Hash(formData.confirmPassword),
        userID: roleId[0], // !imp uncomment
      }

      const response = await changePassword(payload) // Call your custom API function here

      setLoading(false)

      if (response.success) {
        localStorage.removeItem('token')

        showToast('Password changed successfully', 'success')

        setFormData({
          newPassword: '',
          confirmPassword: '',
        })

        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        showToast(response.message, 'error')
      }
    }
  }

  const fieldLabels = {
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <div className="card" style={{ border: 'none', borderRadius: 0 }}>
          <LogoHeader />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f4f4f4',
            padding: '2rem 1rem',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '460px',
              backgroundColor: '#ffffff',
              borderRadius: '18px',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                backgroundColor: '#3a0d5f',
                padding: '24px 28px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#4d5f7c',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaLock size={20} color="#ffffff" />
              </div>
              <div>
                <p style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                  Change Password
                </p>
                <p style={{ color: '#C9D1DB', fontSize: '0.8rem', margin: 0 }}>
                  Set a new password for your account
                </p>
              </div>
            </div>

            <div style={{ padding: '28px' }}>
              <form onSubmit={handleSubmit}>
                {['newPassword', 'confirmPassword'].map((field) => (
                  <div className="input-group mb-3" key={field}>
                    <span
                      className="input-group-text"
                      style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB', borderRight: 'none' }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="16"
                        height="16"
                        role="img"
                        aria-hidden="true"
                      >
                        <path
                          fill="#3a0d5f"
                          d="M384,200V144a128,128,0,0,0-256,0v56H88V328c0,92.635,75.364,168,168,168s168-75.365,168-168V200ZM160,144a96,96,0,0,1,192,0v56H160ZM392,328c0,74.99-61.01,136-136,136s-136-61.01-136-136V232H392Z"
                        />
                      </svg>
                    </span>
                    <input
                      type={showPasswords[field] ? 'text' : 'password'}
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                      placeholder={fieldLabels[field]}
                      autoComplete="new-password"
                      required
                      disabled={loading}
                      pattern={field === 'newPassword' ? PASSWORD_PATTERN.source : undefined}
                      style={{
                        padding: '10px 14px',
                        border: '1px solid #E5E7EB',
                        borderLeft: 'none',
                        borderRight: 'none',
                        backgroundColor: loading ? '#F3F4F6' : '#ffffff',
                      }}
                    />
                    <span
                      className="input-group-text"
                      onClick={() => !loading && toggleShowPassword(field)}
                      style={{
                        backgroundColor: '#F3F4F6',
                        border: '1px solid #E5E7EB',
                        borderLeft: 'none',
                        cursor: loading ? 'not-allowed' : 'pointer',
                      }}
                      aria-label={`Toggle ${field} visibility`}
                    >
                      {showPasswords[field] ? <FaEyeSlash color="#6B7280" /> : <FaEye color="#6B7280" />}
                    </span>
                    {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
                  </div>
                ))}
                <button
                  className="btn w-100"
                  style={{
                    backgroundColor: '#3a0d5f',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '10px',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(60, 75, 100, 0.25)',
                  }}
                  type="submit"
                  disabled={loading || !userId || Object.values(errors).some((error) => error)}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </button>
              </form>

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: '10px',
                }}
              >
                <ul style={{ listStyle: 'square', color: 'red', fontSize: '13px', marginBottom: 0, paddingLeft: '18px' }}>
                  <strong>Password requirements:</strong>
                  <li>At least 8 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one number</li>
                  <li>No white spaces</li>
                  <li>At least one special character (@, #, $, ?, etc.)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Tostify />
    </>
  );
}

export default ConfirmNewPassword;